import React, { useState, useEffect } from 'react';
import { X, Printer, Bell, Send, CheckCircle, AlertCircle, Loader2, MessageSquare } from 'lucide-react';
import { supportTicketService } from '../services/api';

export const Modal = ({ title, onClose, children, maxWidth = 'max-w-2xl' }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
    <div className={`bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl w-full ${maxWidth} max-h-[90vh] flex flex-col overflow-hidden`}>
      <div className="px-6 py-4 border-b border-slate-800 flex justify-between items-center">
        <h3 className="text-lg font-black text-white">{title}</h3>
        <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition"><X size={20} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-6">{children}</div>
    </div>
  </div>
);

export const StatementsModal = ({ show, onClose, stats, allInvestments, recentInvestments, onPrint }) => {
  if (!show) return null;
  const investments = allInvestments || recentInvestments || [];
  return (
    <Modal title="Investment Statements" onClose={onClose} maxWidth="max-w-4xl">
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-slate-950/50 rounded-2xl p-4 border border-slate-800">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Total Invested</p>
            <p className="text-2xl font-black text-white mt-2">₹{(stats.total_invested || 0).toLocaleString()}</p>
          </div>
          <div className="bg-slate-950/50 rounded-2xl p-4 border border-slate-800">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Total Returns</p>
            <p className="text-2xl font-black text-red-500 mt-2">₹{(stats.total_returns || 0).toLocaleString()}</p>
          </div>
          <div className="bg-slate-950/50 rounded-2xl p-4 border border-slate-800">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Pending Payouts</p>
            <p className="text-2xl font-black text-amber-500 mt-2">₹{(stats.pending_payouts || 0).toLocaleString()}</p>
          </div>
        </div>
        {investments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left py-3 px-2 text-[10px] font-black uppercase tracking-widest text-slate-500">Investment</th>
                  <th className="text-right py-3 px-2 text-[10px] font-black uppercase tracking-widest text-slate-500">Amount</th>
                  <th className="text-right py-3 px-2 text-[10px] font-black uppercase tracking-widest text-slate-500">Current Value</th>
                  <th className="text-right py-3 px-2 text-[10px] font-black uppercase tracking-widest text-slate-500">Returns</th>
                  <th className="text-center py-3 px-2 text-[10px] font-black uppercase tracking-widest text-slate-500">Status</th>
                  <th className="text-center py-3 px-2 text-[10px] font-black uppercase tracking-widest text-slate-500">Certificate</th>
                </tr>
              </thead>
              <tbody>
                {investments.map((inv) => (
                  <tr key={inv.id} className="border-b border-slate-800/50 hover:bg-slate-950/30">
                    <td className="py-3 px-2">
                      <p className="font-bold text-white">#{inv.investment_number || inv.id}</p>
                      <p className="text-[10px] text-slate-500 uppercase">{inv.type?.replace('_', ' ')}</p>
                    </td>
                    <td className="text-right py-3 px-2 font-bold text-white">₹{(inv.amount || 0).toLocaleString()}</td>
                    <td className="text-right py-3 px-2 font-bold text-white">₹{(inv.current_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className={'text-right py-3 px-2 font-bold ' + ((inv.profit_loss || 0) >= 0 ? 'text-red-500' : 'text-rose-500')}>
                      ₹{(inv.profit_loss || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="text-center py-3 px-2">
                      <span className={'text-[10px] font-black uppercase tracking-widest ' + (inv.status === 'active' ? 'text-red-500' : inv.status === 'matured' ? 'text-blue-500' : 'text-amber-500')}>{inv.status}</span>
                    </td>
                    <td className="text-center py-3 px-2">
                      <button onClick={() => onPrint(inv)} className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 transition-all" title="Download Certificate">
                        <Printer size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-slate-500 italic">No investments found.</div>
        )}
      </div>
    </Modal>
  );
};

export const AllInvestmentsModal = ({ show, onClose, allInvestments, recentInvestments, onPrint }) => {
  if (!show) return null;
  const investments = allInvestments || recentInvestments || [];
  return (
    <Modal title="All Investments" onClose={onClose} maxWidth="max-w-4xl">
      <div className="space-y-4">
        {investments.length > 0 ? (
          investments.map((inv) => (
            <div key={inv.id} className="p-4 rounded-2xl bg-slate-950/50 border border-slate-800/50 hover:border-red-500/30 transition-all">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center text-red-500 font-bold border border-slate-800">INV</div>
                  <div>
                    <p className="text-sm font-bold text-white">#{inv.investment_number || inv.id}</p>
                    <p className="text-[10px] text-slate-500 font-bold uppercase">{inv.type?.replace('_', ' ')} - {inv.interest_rate}% ROI</p>
                  </div>
                </div>
                <span className={'text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full ' + (inv.status === 'active' ? 'bg-red-500/10 text-red-500' : inv.status === 'matured' ? 'bg-blue-500/10 text-blue-500' : 'bg-amber-500/10 text-amber-500')}>{inv.status}</span>
              </div>
              <div className="grid grid-cols-4 gap-3 text-xs">
                <div>
                  <p className="text-[10px] font-black uppercase text-slate-500">Principal</p>
                  <p className="font-bold text-white mt-1">₹{(inv.amount || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase text-slate-500">Current Value</p>
                  <p className="font-bold text-white mt-1">₹{(inv.current_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase text-slate-500">Returns</p>
                  <p className={'font-bold mt-1 ' + ((inv.profit_loss || 0) >= 0 ? 'text-red-500' : 'text-rose-500')}>₹{(inv.profit_loss || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase text-slate-500">Start Date</p>
                  <p className="font-bold text-white mt-1">{inv.start_date ? new Date(inv.start_date).toLocaleDateString() : '—'}</p>
                </div>
              </div>
              <div className="flex justify-end mt-3">
                <button onClick={() => onPrint(inv)} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white font-bold text-xs hover:bg-red-700 transition-all">
                  <Printer size={14} /> Download Certificate
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12 text-slate-500 italic">No investments found.</div>
        )}
      </div>
    </Modal>
  );
};

export const AnnouncementsModal = ({ show, onClose, announcements }) => {
  if (!show) return null;
  return (
    <Modal title="Platform Announcements" onClose={onClose} maxWidth="max-w-2xl">
      <div className="space-y-4">
        {announcements && announcements.length > 0 ? (
          announcements.map((ann, idx) => (
            <div key={ann.id || idx} className="p-5 rounded-2xl bg-slate-950/50 border border-slate-800/50">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Bell size={16} className="text-red-500" />
                  <h4 className="text-sm font-bold text-white">{ann.title}</h4>
                </div>
                <span className="text-[10px] text-slate-500 font-bold uppercase">{new Date(ann.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">{ann.message}</p>
              {!ann.is_read && (
                <span className="inline-block mt-3 text-[10px] font-black uppercase tracking-widest text-red-500 bg-red-500/10 px-3 py-1 rounded-full">New</span>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-12 text-slate-500 italic">
            <Bell size={32} className="mx-auto mb-4 text-slate-700" />
            No announcements at this time. Check back later for updates.
          </div>
        )}
      </div>
    </Modal>
  );
};

export const SupportTicketFormModal = ({ show, onClose }) => {
  const [form, setForm] = useState({ subject: '', category: 'other', priority: 'medium', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject || !form.description) return;
    setSubmitting(true);
    setMsg(null);
    try {
      const res = await supportTicketService.store(form);
      setMsg({ type: 'success', text: 'Ticket ' + (res.ticket?.ticket_number || 'created') + ' successfully!' });
      setForm({ subject: '', category: 'other', priority: 'medium', description: '' });
      setTimeout(() => { onClose(); setMsg(null); }, 2000);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed to create ticket. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Create Support Ticket" onClose={() => { onClose(); setMsg(null); }} maxWidth="max-w-lg">
      {msg && (
        <div className={'mb-4 p-4 rounded-2xl flex items-center gap-3 ' + (msg.type === 'success' ? 'bg-red-500/10 text-red-500 border border-red-500/20' : 'bg-rose-500/10 text-rose-500 border border-rose-500/20')}>
          {msg.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <p className="text-sm font-bold">{msg.text}</p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Subject</label>
          <input type="text" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:border-red-500 focus:outline-none transition" placeholder="Brief description of your issue" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:border-red-500 focus:outline-none transition">
              <option value="payment">Payment</option>
              <option value="investment">Investment</option>
              <option value="order">Order</option>
              <option value="account">Account</option>
              <option value="technical">Technical</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Priority</label>
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:border-red-500 focus:outline-none transition">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Description</label>
          <textarea required rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:border-red-500 focus:outline-none transition resize-none" placeholder="Describe your issue in detail..." />
        </div>
        <button type="submit" disabled={submitting} className="w-full py-3 rounded-xl bg-red-600 text-white font-black text-sm hover:bg-red-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          {submitting ? 'Submitting...' : 'Submit Ticket'}
        </button>
      </form>
    </Modal>
  );
};

export const SupportTicketListModal = ({ show, onClose }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (show) {
      setLoading(true);
      supportTicketService.index().then((data) => {
        setTickets(Array.isArray(data) ? data : (data.data || []));
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [show]);

  if (!show) return null;

  const statusColor = (status) => {
    if (status === 'open') return 'text-red-500 bg-red-500/10';
    if (status === 'in_progress') return 'text-amber-500 bg-amber-500/10';
    if (status === 'resolved') return 'text-blue-500 bg-blue-500/10';
    return 'text-slate-500 bg-slate-500/10';
  };

  return (
    <Modal title="My Support Tickets" onClose={onClose} maxWidth="max-w-2xl">
      {loading ? (
        <div className="text-center py-12">
          <Loader2 size={24} className="animate-spin mx-auto text-red-500" />
        </div>
      ) : tickets.length > 0 ? (
        <div className="space-y-3">
          {tickets.map((ticket) => (
            <div key={ticket.id} className="p-4 rounded-2xl bg-slate-950/50 border border-slate-800/50">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm font-bold text-white">{ticket.subject}</p>
                  <p className="text-[10px] text-slate-500 font-bold uppercase mt-1">{ticket.ticket_number} - {ticket.category}</p>
                </div>
                <span className={'text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full ' + statusColor(ticket.status)}>
                  {ticket.status.replace('_', ' ')}
                </span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed mt-2">{ticket.description}</p>
              {ticket.admin_response && (
                <div className="mt-3 p-3 rounded-xl bg-slate-900 border border-slate-800">
                  <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mb-1">Admin Response</p>
                  <p className="text-xs text-slate-300">{ticket.admin_response}</p>
                </div>
              )}
              <p className="text-[10px] text-slate-500 font-bold uppercase mt-2">{new Date(ticket.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-slate-500 italic">
          <MessageSquare size={32} className="mx-auto mb-4 text-slate-700" />
          No support tickets yet. Create one if you need help.
        </div>
      )}
    </Modal>
  );
};
